import { lumi } from "./lumi"

/**
 * 사용자 프로필(userProfiles3) 중복 생성 방지 및 대표 프로필 선택 공용 유틸
 *
 * 배경:
 * - 기존 코드는 프로필 조회 시 정렬 없이 limit 1만 사용해, 같은 userId에
 *   레코드가 2건 이상이면 매번 다른 레코드가 잡히는 문제가 있었다.
 * - 조회가 일시적으로 실패해도 "프로필 없음"으로 간주해 pending 프로필을
 *   새로 생성하면서 중복이 계속 늘어나는 구조였다.
 *
 * 이 모듈은 위 두 가지를 한 곳에서 차단한다.
 */

export type UserProfile = {
  _id: string
  userId: string
  email?: string
  userName?: string
  teamId?: string
  teamName?: string
  hasSelectedTeam?: boolean
  approvalStatus?: string
  role?: string
  enabled?: boolean
  createdAt?: string
  updatedAt?: string
  [key: string]: any
}

/** 조회 실패와 "프로필 없음"을 구분하기 위한 결과 타입 */
export type ProfileLookup = {
  /** 조회 자체가 성공했는지 (false면 프로필 유무를 판단할 수 없음) */
  ok: boolean
  /** 조회된 전체 프로필 목록 (오래된 순) */
  list: UserProfile[]
  /** 대표 프로필 (없으면 null) */
  primary: UserProfile | null
  /** 중복 레코드 존재 여부 */
  hasDuplicates: boolean
  error?: unknown
}

const APPROVAL_SCORE: Record<string, number> = {
  approved: 100,
  pending: 40,
  rejected: 0,
}

const ROLE_SCORE: Record<string, number> = {
  ADMIN: 4,
  C_LEVEL: 3,
  MANAGER: 2,
  USER: 1,
}

/**
 * 여러 프로필 중 "실제로 사용해야 할" 대표 프로필을 결정한다.
 * 승인 상태 > 활성 여부 > 팀 선택 여부 > 역할 > 생성일(오래된 것 우선) 순으로 평가한다.
 * 생성일이 오래된 레코드를 우선하는 이유는, 나중에 실수로 생긴 레코드가 아닌
 * 원본 계정 정보를 유지하기 위함이다.
 */
export function scoreProfile(profile: UserProfile): number {
  let score = 0
  score += APPROVAL_SCORE[profile?.approvalStatus || ""] ?? 20
  if (profile?.enabled !== false) score += 10
  if (profile?.hasSelectedTeam === true) score += 8
  if (profile?.teamId) score += 4
  if (profile?.userName) score += 2
  score += ROLE_SCORE[profile?.role || ""] ?? 0
  return score
}

export function pickPrimaryProfile(list: UserProfile[]): UserProfile | null {
  if (!Array.isArray(list) || list.length === 0) return null
  if (list.length === 1) return list[0]

  return [...list].sort((a, b) => {
    const diff = scoreProfile(b) - scoreProfile(a)
    if (diff !== 0) return diff
    // 점수가 같으면 먼저 만들어진 레코드를 원본으로 본다
    const at = a?.createdAt || ""
    const bt = b?.createdAt || ""
    return at.localeCompare(bt)
  })[0]
}

/**
 * userId 기준 프로필을 "전부" 조회한다.
 * limit 1을 사용하지 않으므로 중복 레코드를 감지할 수 있고,
 * 조회 실패 시 ok=false로 반환해 호출부가 신규 생성을 하지 않도록 한다.
 */
export async function fetchProfilesByUserId(
  userId: string,
  projection?: Record<string, number>
): Promise<ProfileLookup> {
  if (!userId) {
    return { ok: false, list: [], primary: null, hasDuplicates: false }
  }

  try {
    const params: Record<string, any> = {
      filter: { userId },
      sort: { createdAt: 1 },
      limit: 50,
    }
    if (projection) params.projection = projection

    const result = await lumi.entities.userProfiles3.list(params)
    const list = (result?.list || []) as UserProfile[]

    if (list.length > 1) {
      console.warn(
        `⚠️ 중복 프로필 감지: userId=${userId}, ${list.length}건`,
        list.map((p) => ({
          _id: p._id,
          approvalStatus: p.approvalStatus,
          teamName: p.teamName,
          createdAt: p.createdAt,
        }))
      )
    }

    return {
      ok: true,
      list,
      primary: pickPrimaryProfile(list),
      hasDuplicates: list.length > 1,
    }
  } catch (error) {
    console.error("❌ 프로필 조회 실패:", error)
    return { ok: false, list: [], primary: null, hasDuplicates: false, error }
  }
}

/**
 * 프로필이 없을 때만 신규 생성한다.
 *
 * 중복 방지 3중 장치:
 * 1) 조회 실패(ok=false) 시에는 절대 생성하지 않는다.
 * 2) 이미 프로필이 있으면 생성하지 않고 기존 대표 프로필을 반환한다.
 * 3) 생성 직전에 한 번 더 재조회하여, 동시 요청으로 인한 중복 생성을 막는다.
 */
export async function createProfileIfMissing(input: {
  userId: string
  email?: string
  userName?: string
}): Promise<{ created: boolean; profile: UserProfile | null; blocked?: string }> {
  const { userId, email, userName } = input

  if (!userId) {
    return { created: false, profile: null, blocked: "no-user-id" }
  }

  const lookup = await fetchProfilesByUserId(userId)

  // 1) 조회 실패 시 생성 금지 (일시적 오류로 중복 계정이 생기는 것을 차단)
  if (!lookup.ok) {
    console.warn("⚠️ 프로필 조회 실패 - 신규 생성을 건너뜁니다. (중복 생성 방지)")
    return { created: false, profile: null, blocked: "lookup-failed" }
  }

  // 2) 이미 존재하면 생성 금지
  if (lookup.primary) {
    return { created: false, profile: lookup.primary, blocked: "already-exists" }
  }

  // 3) 생성 직전 재확인 (동시 로그인 등 경합 상황 방어)
  const recheck = await fetchProfilesByUserId(userId)
  if (!recheck.ok) {
    return { created: false, profile: null, blocked: "recheck-failed" }
  }
  if (recheck.primary) {
    return { created: false, profile: recheck.primary, blocked: "already-exists" }
  }

  const now = new Date().toISOString()
  const created = await lumi.entities.userProfiles3.create({
    userId,
    email: email || "",
    userName: userName || "",
    teamId: "",
    teamName: "",
    hasSelectedTeam: false,
    approvalStatus: "pending",
    enabled: true,
    role: "USER",
    createdAt: now,
    updatedAt: now,
  })

  console.log("✅ 신규 프로필 생성 완료:", userId)
  return { created: true, profile: created as UserProfile }
}
